import type { GalaxyPackage } from '@/types/galaxy-data/packages'
import { parse } from 'date-fns'
import { isDateInRange } from './utils'
import type { GalaxyMeetingRoom } from '@/types/galaxy-data/meeting-room'
import { useConfigurations } from '@/stores/configurations'

/**
 * Get location from query param and validate
 * that it is in the list of available locations
 */
export function getLocationFromUrl(locationList: { value: string | number }[]) {
  const urlSearchParams = new URLSearchParams(window.location.search)
  const location = urlSearchParams.get('location')

  if (location && locationList.find((item) => String(item.value) === location)) {
    return location
  }

  return ''
}

/**
 * Get date from query param and validate
 * Depending on the min/max Date and this format
 * A default value is applied through "minDate" parameter
 */
export function getDateFromUrl(minDate?: Date, maxDate?: Date, dateFormat = 'yyyy-MM-dd') {
  const urlSearchParams = new URLSearchParams(window.location.search)
  const stringDate = urlSearchParams.get('date')

  if (stringDate) {
    const date = parse(stringDate, dateFormat, new Date())

    if (isDateInRange(date, minDate, maxDate)) {
      return date
    }
  }

  return minDate
}

/**
 * Get attendees number from query param and validate
 * that it is in the range of allowed number
 * A default value is applied through "defaultValue" parameter
 */
export function getAttendeesFromUrl(min = 1) {
  const configStore = useConfigurations()

  const urlSearchParams = new URLSearchParams(window.location.search)
  const attendees = urlSearchParams.get('attendees')
  const defaultValue =
    configStore.hotelSearch.defaultNbAttendees ?? configStore.funnel.defaultNbAttendees ?? 10

  if (!attendees) {
    return defaultValue
  }

  const nbAttendees = parseInt(attendees)
  if (isNaN(nbAttendees)) {
    return defaultValue
  }
  if (nbAttendees < min) {
    return defaultValue
  }
  return nbAttendees
}

/**
 * Get packageID from query param and validate
 */
export function getPackageIDFromUrl(galaxyPackages?: GalaxyPackage[]) {
  const urlSearchParams = new URLSearchParams(window.location.search)
  const packageId = urlSearchParams.get('packageID')

  if (!galaxyPackages || packageId?.length === 0) {
    return packageId
  }
  return galaxyPackages.find((galaxyPackage) => galaxyPackage.db2_packageid === packageId)
}

/**
 * Get meetingRoomID from query param and validate
 */
export function getMeetingRoomFromUrl(galaxyMeetingRooms?: GalaxyMeetingRoom[]) {
  const urlSearchParams = new URLSearchParams(window.location.search)
  const meetingRoomId = urlSearchParams.get('meetingRoomID')

  if (!galaxyMeetingRooms) {
    return meetingRoomId
  }
  return galaxyMeetingRooms.find(
    (galaxyMeetingRoom) => galaxyMeetingRoom.db2_eventroomid === meetingRoomId,
  )
}
